import {
  Dialog as MUIDialog,
  DialogProps as MUIDialogProps,
} from '@mui/material';
import { useCallback } from 'react';

// MUI closes only the topmost modal on Escape keydown, including child menus.
type RelevantMUIDialogProps = Omit<MUIDialogProps, 'disableEscapeKeyDown'>;

export interface DialogProps extends RelevantMUIDialogProps {
  onClose?: () => void;
  /**
   * If true, clicking outside the modal (backdrop click) will not close the modal.
   * Users must explicitly click a button or the X to close.
   */
  disableBackdropClick?: boolean;
}

export function Dialog({
  open,
  onClose,
  disableBackdropClick,
  ...props
}: DialogProps) {
  // Handle onClose to filter out backdrop clicks if disableBackdropClick is true
  const handleClose = useCallback(
    (_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
      // If backdrop clicks are disabled and this is a backdrop click, don't close
      if (disableBackdropClick && reason === 'backdropClick') {
        return;
      }
      // Otherwise, call the onClose handler if it exists
      if (onClose) {
        onClose();
      }
    },
    [disableBackdropClick, onClose]
  );

  return (
    <MUIDialog
      open={open}
      onClose={handleClose}
      // Without a close action, let editor shortcuts receive Escape.
      disableEscapeKeyDown={!onClose}
      {...props}
    />
  );
}
